import React, { useState } from 'react';
import { Footer, Header, TripleAction, Content, ContentSection } from '../../gs-component-right-rail';
import { RadioButton, SingleSelectGroup, Checkbox, ToggleSwitch } from '@xo-union/tk-component-switches';
import Icon from '@xo-union/tk-component-icons';
import BlankButton from '@xo-union/component-blank-button';
import { Subtle, Body1 } from '@xo-union/tk-ui-typography';
import { compose } from '@xo-union/react-css-modules';
import { DisplayLink } from '@xo-union/tk-ui-links';
import { onSave } from '../helpers/onSave';
import styles from './rsvpSettingsRightRail.scss';
import { RSVPSettingsRailProps } from '.';

type Props = Omit<RSVPSettingsRailProps, 'showRail'>;

const RSVPSettings: React.FC<Props> = ({
  wwsState,
  rsvpOnInEvents,
  hasPrivateEvent,
  onClickEditForm,
  onClose,
  guestWedding,
  onSuccess,
}) => {
  const initialRsvpHidden = guestWedding?.rsvpPageHidden ?? false;
  const initialAllowGuestPreview = guestWedding?.allowGuestPreview ?? false;
  const initialIsPrivateRsvp = guestWedding?.isPrivateRsvp ?? false;

  const [rsvpPageHidden, setRsvpPageHidden] = useState(initialRsvpHidden.toString());
  const [allowGuestPreview, setAllowGuestPreview] = useState(initialAllowGuestPreview.toString());
  const [isPrivateRsvp, setIsPrivateRsvp] = useState(initialIsPrivateRsvp.toString());

  const renderPageVisibility = (): JSX.Element => (
    <ContentSection
      as="h2"
      name="Page Visibility"
      inlineChild={
        <>
          {rsvpOnInEvents && (
            <ToggleSwitch
              checked={rsvpPageHidden === 'false'}
              onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                setRsvpPageHidden((!e.target.checked).toString());
              }}
              data-testid="rsvp-visibility-toggle"
            >
              {rsvpPageHidden === 'false' ? 'Visible' : 'Hidden'}
            </ToggleSwitch>
          )}
        </>
      }
    >
      {rsvpOnInEvents ? (
        <Subtle>No one can RSVP from your Website until this page is visible</Subtle>
      ) : (
        <div className={styles.subTextWithLink}>
          <Subtle>Heads up! You must have ‘Collect RSVPs’ on for at least one event to make this page visible</Subtle>
          <DisplayLink onClick={onClickEditForm} as={BlankButton}>
            <Body1>Edit Form</Body1>
          </DisplayLink>
        </div>
      )}
    </ContentSection>
  );

  if (!guestWedding) {
    return null;
  }

  return (
    <>
      <Header onClose={onClose} as="h1">
        RSVP Settings
      </Header>
      {!(wwsState === 'published') && (
        <div className={styles.noSite}>
          You need a Wedding Website to use Online RSVP
          <DisplayLink className={styles.link} href={process.env.WWS_DASHBOARD_URL}>
            Get Started
            <Icon name="linkout" size="sm" />
          </DisplayLink>
        </div>
      )}
      <Content className={styles.rightRailContent}>
        {wwsState === 'published' && renderPageVisibility()}
        <ContentSection name="Access" as="h2">
          <SingleSelectGroup
            value={isPrivateRsvp}
            onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
              setIsPrivateRsvp(e.target.value);
            }}
          >
            <RadioButton classes={compose({ container: styles.radioButton })} value="true">
              Guest List Only <Subtle>(recommended)</Subtle>
            </RadioButton>
            <Subtle className={styles.subtext}>Only guests on your Guest List can RSVP.</Subtle>
            <RadioButton classes={compose({ container: styles.radioButton })} value="false">
              Public RSVP <Subtle>(not recommended)</Subtle>
            </RadioButton>
            <Subtle className={styles.subtext}>
              {hasPrivateEvent
                ? 'Anybody can RSVP for your events, including events that are only open to select guests.'
                : 'Anyone with the link to your Wedding Website can RSVP.'}
            </Subtle>
          </SingleSelectGroup>
        </ContentSection>
        <ContentSection name="Guest List Visibility" as="h2">
          <Checkbox
            checked={allowGuestPreview === 'true'} // converts to bool
            onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
              setAllowGuestPreview(e.currentTarget.checked.toString());
            }}
          >
            <Body1>Allow anybody who RSVPs &quot;Yes!&quot; to see who&apos;s attending each event</Body1>
          </Checkbox>
        </ContentSection>
      </Content>
      <Footer>
        <TripleAction
          primaryText="Save"
          primaryOnClick={(): void => {
            onSave(rsvpPageHidden, allowGuestPreview, isPrivateRsvp, onSuccess);
          }}
          secondaryText="Cancel"
          secondaryOnClick={onClose}
        />
      </Footer>
    </>
  );
};

export default RSVPSettings;
